import { Router } from "@angular/router";
import { vGlobal } from "@core/types/cb-global-vars";
import { environment } from "environments/environment";
import { Component, Inject, OnInit } from "@angular/core";
import { ApiService } from "@core/services/api/api.service";
import { SignOutService } from "app/modules/public/sign-out/sign-out.service";
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog, } from "@angular/material/dialog";
import { manageStorageService } from "@core/services/manage-storage/manage-storage.service";

/**
 * Componente que muestra un diálogo de error personalizado según el código
 * de error HTTP recibido. Gestiona la visualización de mensajes, el tipo de
 * alerta (error/warning) y las acciones de redirección o cierre de sesión
 * dependiendo del código y contexto del error.
 */
@Component({
  selector: "error-dialog",
  styleUrls: ["./errordialog.component.scss"],
  templateUrl: "./errordialog.component.html",
})
export class ErrorDialogComponent implements OnInit {
  /** Tipo de alerta visual: "error" | "warning". Define el estilo del diálogo. */
  typeOfAlert: string = "error";

  /** Indica si se deben mostrar los detalles técnicos del error en producción. */
  showErrDetail: boolean = !environment.production;

  /**
   * Inicializa el componente con las dependencias inyectadas.
   * @param dataErr          Datos del error recibidos a través del diálogo Material.
   * @param _errorModal      Referencia al diálogo modal actual para cerrarlo.
   * @param _router          Servicio de enrutamiento de Angular.
   * @param _api             Servicio API para operaciones como cierre de sesión.
   * @param allModals        Referencia a todos los diálogos abiertos para cerrarlos globalmente.
   * @param _signOutS        Servicio que maneja el proceso de cierre de sesión.
   * @param _mStorage        Servicio de gestión de almacenamiento local.
   */
  constructor(
    @Inject(MAT_DIALOG_DATA) public dataErr: any,
    private _errorModal: MatDialogRef<ErrorDialogComponent>, // this window
    private _router: Router,
    private _api: ApiService,
    private allModals: MatDialog, // all windows
    private _signOutS: SignOutService,
    private _mStorage: manageStorageService,
  ) { }


  /**
   * Ciclo de vida de Angular que se ejecuta al iniciar el componente.
   * Evalúa el código de error recibido (`codeError`) para:
   * - Asignar un mensaje amigable al usuario.
   * - Definir el tipo de alerta visual (error/warning).
   * - Ejecutar acciones automáticas como cierre de sesión o redirección.
   */
  ngOnInit() {

    if (this.dataErr?.codeError >= 500) {

      this.dataErr.message =
        "Ocurrió un error de comunicación, por favor intenta nuevamente";
      this.typeOfAlert = "error";
    }

    
    switch (this.dataErr?.codeError) {
      case 0:
        this.dataErr.message =
          "Sin conexión al servidor, revisa si los CORS están deshabilitados";
        this.typeOfAlert = "warning";
        break;
      case 400:
        this.typeOfAlert = "error";
        break;
      case 204: // warning case
        this.typeOfAlert = "warning";
        break;
      case 404:
        this.dataErr.message = "Recurso no encontrado.";
        this.typeOfAlert = "warning";
        break;
      case 419:
      case 401:
        setTimeout(() => this.closeDialogError('close'), 15000);
        this.dataErr.message = "Tu sesión ha expirado. Si lo deseas, ingresa nuevamente.";
        this.typeOfAlert = "error";
        break;
      case 403:
        this.dataErr.message = (this.dataErr.wsName == 'init') ? 'Aviso: servicio no disponible por el momento.' : this.dataErr.message;
        this.typeOfAlert = "warning";
        break;
      case 408:
      case 409: // error case

        if (vGlobal.wsErrorLogout.includes(this.dataErr.wsName)) {
          setTimeout(() => this.allModals.closeAll(), 500);
          this._api.logOut('navigate');
        }

        this.typeOfAlert = "error";
        break;
      case 423:
        if (this.dataErr.wsName != 'init') {
          setTimeout(() => this.closeDialogError('close'), 10000);
        }
        this.typeOfAlert = "error";
        break;
    }


  }

  /**
   * Cierra el diálogo de error y ejecuta acciones adicionales según el
   * código de error almacenado. Entre las acciones posibles están:
   * - Redireccionar al login (errores 409/412/423).
   * - Cerrar todas las ventanas modales y cerrar sesión (errores 401/419/423).
   *
   * @param action - Acción a realizar al cerrar. Por defecto `'close'`.
   *                 Si tiene valor, cierra el modal y retorna los datos.
   */
  closeDialogError(action: string = 'close') {
    this.dataErr.confirm = action;

    if (action) { this._errorModal.close(this.dataErr); }

    switch (this.dataErr.codeError) {
      case 409:
        switch (this.dataErr.wsName) {
          case 'autenticacion':
            let caseToRedirect = ['llegado', 'desbloqueo'],
              includesToRedirect = (findWord: string[]) => {
                let stringLower = this.dataErr.message?.toLowerCase(),
                  finders = findWord.filter(text =>
                    stringLower.includes(text.toLowerCase())
                  );
                return finders.length >= 1;
              }

            if (includesToRedirect(caseToRedirect)) {
              this._mStorage.clearStorage('s_unblockUser')
              this._router.navigate(['/login']);
            }

            break;
        }
        break;

      case 401:
      case 419:
        setTimeout(() => this.allModals.closeAll(), 1000);
        this._api.logOut()
          .then(() => this._signOutS.processToLogOut(), () => this._signOutS.processToLogOut())
          .catch((errLgOut) => { this._signOutS.processToLogOut(); console.error(errLgOut) })
        break;

      case 423:
        if (this.dataErr.wsName != 'init') {
          setTimeout(() => this.allModals.closeAll(), 1000);
          this._api.logOut()
            .then(() => this._signOutS.processToLogOut(), () => this._signOutS.processToLogOut())
            .catch((errLgOut) => { this._signOutS.processToLogOut(); console.error(errLgOut) })
        } else { this._router.navigate(['unblock-user']) }
        break;
      case 412:
        this._mStorage.clearStorage('s_unblockUser')
        this._router.navigate(['/login']);
        break;
    }

  }
}
